import React, { createContext, useContext, useReducer, useEffect } from "react";

const SETTINGS_STORAGE_KEY = "dareme-app-settings";

// --- Definicja stanu początkowego ---
const initialSettings = {
  theme: "system", // 'light', 'dark', 'system'
  reduceMotion: false, // Czy redukować animacje
  soundEnabled: true, // Czy dźwięki aplikacji są włączone
  vibrationEnabled: true, // Czy wibracje są włączone (głównie mobilne)
  autoSaveGame: true, // Czy stan gry ma być automatycznie zapisywany
};

// --- Funkcja wczytująca ustawienia z localStorage ---
const loadSettings = () => {
  try {
    const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      // Upewnij się, że wszystkie klucze z initialSettings istnieją
      return { ...initialSettings, ...parsed };
    }
  } catch (error) {
    console.error("Błąd wczytywania ustawień aplikacji:", error);
  }
  return initialSettings;
};

// --- Reducer dla ustawień ---
function settingsReducer(state, action) {
  switch (action.type) {
    case "SET_THEME":
      return { ...state, theme: action.payload };
    case "TOGGLE_REDUCE_MOTION":
      return { ...state, reduceMotion: !state.reduceMotion };
    case "SET_REDUCE_MOTION": // Dla bezpośredniego ustawienia
      return { ...state, reduceMotion: action.payload };
    case "TOGGLE_SOUND":
      return { ...state, soundEnabled: !state.soundEnabled };
    case "TOGGLE_VIBRATION":
      return { ...state, vibrationEnabled: !state.vibrationEnabled };
    case "TOGGLE_AUTOSAVE_GAME":
      return { ...state, autoSaveGame: !state.autoSaveGame };
    case "UPDATE_SETTINGS": // Ogólna akcja do aktualizacji wielu ustawień
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

// --- Tworzenie kontekstu ---
const SettingsContext = createContext(null);

// --- Komponent Provider ---
export function SettingsProvider({ children }) {
  const [settings, dispatchSettings] = useReducer(
    settingsReducer,
    initialSettings,
    loadSettings
  );

  // Efekt do zapisywania ustawień w localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Błąd zapisywania ustawień aplikacji:", error);
    }
  }, [settings]);

  // Efekt do zarządzania motywem (jasny/ciemny/systemowy)
  useEffect(() => {
    const root = window.document.documentElement;
    const isDark =
      settings.theme === "dark" ||
      (settings.theme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    root.classList.remove(isDark ? "light" : "dark");
    root.classList.add(isDark ? "dark" : "light");
  }, [settings.theme]);

  // Efekt do zarządzania redukcją ruchu
  useEffect(() => {
    const root = window.document.documentElement;
    if (settings.reduceMotion) {
      root.setAttribute("data-reduce-motion", "true");
    } else {
      root.removeAttribute("data-reduce-motion");
    }
    // Dodatkowo, możesz chcieć nasłuchiwać systemowego ustawienia preferencji:
    // const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    // const handleChange = () => dispatchSettings({ type: 'SET_REDUCE_MOTION', payload: mediaQuery.matches });
    // mediaQuery.addEventListener('change', handleChange);
    // return () => mediaQuery.removeEventListener('change', handleChange);
    // Jednak Twoje ustawienie w aplikacji powinno mieć priorytet.
  }, [settings.reduceMotion]);

  return (
    <SettingsContext.Provider value={{ settings, dispatchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

// --- Hook do używania kontekstu ---
export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings musi być używane wewnątrz SettingsProvider");
  }
  return context;
}
